/**
 * Dettaglio newsletter ADMIN (Sprint 18, §8.6).
 * Adattato al nuovo modello/stati reali (DRAFT/SCHEDULED/SENDING/SENT/STOPPED/CANCELLED/FAILED).
 *
 * Layout UNICO: la stessa struttura a form serve sia per la VISTA sia per la MODIFICA.
 *  - Vista     → tutti gli input sono read-only/disabilitati.
 *  - Modifica  → (toggle, solo DRAFT/SCHEDULED) gli input diventano editabili.
 * Campi: nome, nome mittente, destinatari (filtro⇄categoria), reply-to, oggetto, corpo.
 *
 * Lifecycle per stato:
 *  - DRAFT      → Modifica, Invia ora, Programma, Invia test, Elimina
 *  - SCHEDULED  → Modifica, Invia ora, Annulla programmazione, Invia test, Elimina
 *  - SENDING    → Interrompi (stop)
 *  - SENT/STOPPED/CANCELLED/FAILED → sola lettura
 *
 * Modifica/elimina/annulla/schedula sono consentiti dal BE solo in DRAFT/SCHEDULED (altrimenti 409).
 */
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {
  Card,
  CardHeader,
  Container,
  Typography,
  Grid,
  Stack,
  Divider,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsightsIcon from '@mui/icons-material/Insights';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
//--------------------------------------------------------
import { RedOutlinedButton, DefaultOutlinedButton, DefaultContainedButton } from '../../../utils/Buttons';
import { HttpErrorHandler, ErrorUI } from '../../../utils/ErrorHandler';
import { CustomTextField, CustomDatePicker, CustomTimeField } from '../../../utils/StyledInputs';
import { LoadingUI } from '../../../utils/LayoutStyles';
import { usePopup } from '../../../context/PopupContext';
import { newslettersApi } from '../../../services/newsletter/newslettersApi';
import { mediaApi } from '../../../services/newsletter/mediaApi';
import { ensureUnsubscribePlaceholder, inlineEmailHtml, stripUnsubscribeBlock } from '../../../utils/newsletter/emailHtml';
import { buildAttachmentFormData } from '../../../utils/newsletter/attachments';
import { useSendProgress } from '../../../hooks/useSendProgress';
import { StatusBadge } from './components/StatusBadge';
import { HtmlEditor } from './components/HtmlEditor/HtmlEditor';
import { ReplyToSelector } from './components/ReplyToSelector';
import { RecipientSelector } from './components/RecipientSelector';
import { AttachmentsCard } from './components/AttachmentsCard';
import { MediaLibraryDialog } from './components/MediaLibraryDialog';
import {
  composeScheduleDateTime,
  isScheduleTooSoon,
  isToday,
  minScheduleDate,
  minScheduleDateTime,
  scheduleTooSoonMessage,
} from '../../../utils/newsletter/schedule';
import { SendProgressBar } from './components/SendProgressBar';

dayjs.locale('it');

/* Getter difensivi: nuovo modello con fallback al vecchio shape. */
const getStatus = (n) => n.status ?? n.status_name ?? null;
const getName = (n) => n.name ?? n.content?.[0]?.subject ?? '';
const getSubject = (n) => n.subject ?? n.content?.[0]?.subject ?? '';
const getBody = (n) => n.body_html ?? n.content?.[0]?.body ?? '';
const getEntityId = (n) => n.entity_id ?? n.entity?.id ?? null;

const EDITABLE = ['DRAFT', 'SCHEDULED'];

export default function NewsletterDetail() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const newsID = state?.newsID;
  const { showPopup, setPopUpText } = usePopup();

  const [isApiCalled, setIsApiCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [newsletter, setNewsletter] = useState({});
  // Template con spazio contenuto: il corpo è il solo contenuto, il template lo avvolge all'invio.
  const templateWraps = !!newsletter.template_wrapped;
  const [currentProfile, setCurrentProfile] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // modalità modifica inline
  const [isEditing, setIsEditing] = useState(false);

  // menu "Altre azioni"
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const closeActions = () => setActionsAnchor(null);
  const runFromMenu = (fn) => () => {
    closeActions();
    fn();
  };

  // campi del form (sincronizzati col dato; editabili solo in modifica)
  const [name, setName] = useState('');
  const [senderDisplayName, setSenderDisplayName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [recipientMode, setRecipientMode] = useState('filter');
  const [filterId, setFilterId] = useState(null);
  const [recipientCategory, setRecipientCategory] = useState(null);
  const [replyToEmailId, setReplyToEmailId] = useState(null);

  // Allegati
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [removingAttId, setRemovingAttId] = useState(null);
  const [mediaOpen, setMediaOpen] = useState(false);

  // dialog di azione
  const [openDelete, setOpenDelete] = useState(false);
  const [openSchedule, setOpenSchedule] = useState(false);
  const [openTest, setOpenTest] = useState(false);
  const [openDuplicate, setOpenDuplicate] = useState(false);

  // anteprima
  const [openPreview, setOpenPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState(null);
  const [testAddress, setTestAddress] = useState('');

  // Allinea i campi del form al dato corrente (vista = valori reali; annulla = discard).
  const syncForm = (n) => {
    setName(getName(n));
    setSenderDisplayName(n.sender_display_name ?? '');
    setSubject(getSubject(n));
    // Nasconde dall'editor il footer di disiscrizione auto-iniettato (resta nell'email inviata).
    setBodyHtml(stripUnsubscribeBlock(getBody(n)));
    setReplyToEmailId(n.reply_to_email_id ?? null);
    if (n.recipient_category) {
      setRecipientMode('category');
      setRecipientCategory(n.recipient_category);
      setFilterId(null);
    } else {
      setRecipientMode('filter');
      setFilterId(n.filter_id ?? null);
      setRecipientCategory(null);
    }
  };

  const refresh = () => {
    setErrorMessage('');
    newslettersApi
      .get(newsID)
      .then((response) => {
        setNewsletter(response.data);
        syncForm(response.data);
        setExistingAttachments(Array.isArray(response.data.attachments) ? response.data.attachments : []);
        setIsApiCalled(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
        setIsApiCalled(true);
      });
  };

  // Ricarica anche quando si naviga a un'altra newsletter sulla stessa route (es. dopo Duplica).
  useEffect(() => {
    if (newsID) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsID]);

  useEffect(() => {
    let data;
    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));
    if (data) setCurrentProfile(data.roles.filter((role) => role.last_used));
  }, []);

  // Gestione errori applicativi (oltre all'interceptor globale).
  const handleApiError = (err) => {
    const status = err?.response?.status;
    if (status === 409) {
      showPopup(false, 'Operazione non consentita nello stato attuale');
      refresh();
    } else {
      HttpErrorHandler(err, setPopUpText);
      showPopup(false);
    }
  };

  /* ------------------------------- Azioni lifecycle ------------------------------- */
  const runAction = (promise, successMsg, { close } = {}) => {
    setSubmitting(true);
    promise
      .then(() => {
        showPopup(true, successMsg);
        close?.();
        refresh();
      })
      .catch((err) => handleApiError(err))
      .finally(() => setSubmitting(false));
  };

  // Reply-to obbligatorio per invio/programmazione (coerente col composer).
  const requireReplyTo = () => {
    if (!newsletter.reply_to_email_id) {
      showPopup('warning', 'Imposta un indirizzo di risposta (Reply-To) prima dell\'invio. Usa "Modifica".');
      return false;
    }
    return true;
  };

  const onSendNow = () => {
    if (!requireReplyTo()) return;
    runAction(newslettersApi.sendNow(newsID), 'Newsletter in invio');
  };
  const onCancel = () => runAction(newslettersApi.cancel(newsID), 'Programmazione annullata');
  const onStop = () => runAction(newslettersApi.stop(newsID), 'Invio interrotto');

  const onDelete = () => {
    setSubmitting(true);
    newslettersApi
      .remove(newsID)
      .then(() => {
        showPopup(true, 'Newsletter eliminata');
        setTimeout(() => navigate('/admin/newsletter'), 500);
      })
      .catch((err) => handleApiError(err))
      .finally(() => setSubmitting(false));
  };

  const onSchedule = () => {
    if (!requireReplyTo()) return;
    if (!scheduleDate || !scheduleTime) {
      showPopup('warning', 'Inserisci data e ora');
      return;
    }
    // Si può programmare anche per oggi, ma con almeno qualche minuto di anticipo.
    if (isScheduleTooSoon(scheduleDate, scheduleTime)) {
      showPopup('warning', scheduleTooSoonMessage());
      return;
    }
    const dt = composeScheduleDateTime(scheduleDate, scheduleTime);
    runAction(newslettersApi.schedule(newsID, dt.toISOString()), 'Newsletter programmata', {
      close: () => setOpenSchedule(false),
    });
  };

  // Duplica una newsletter (tipicamente annullata) in una NUOVA bozza.
  // Controlli anti-duplicazione: (1) conferma nel dialog, (2) submitting disabilita il bottone,
  // (3) redirect alla nuova bozza dopo la creazione (si lascia la pagina di origine).
  const onDuplicate = () => {
    const payload = {
      entity_id: getEntityId(newsletter),
      name: `Copia di ${getName(newsletter) || 'Newsletter'}`,
      sender_display_name: newsletter.sender_display_name ?? null,
      subject: getSubject(newsletter),
      body_html: getBody(newsletter),
      // Se il template avvolge il contenuto, il duplicato deve restarvi collegato: il corpo da
      // solo non ha intestazione né disiscrizione. La nuova bozza segue il template attuale.
      template_id: templateWraps ? newsletter.template_id : null,
      reply_to_email_id: newsletter.reply_to_email_id ?? null,
      filter_id: newsletter.filter_id ?? null,
      recipient_category: newsletter.recipient_category ?? null,
      scheduled_at: null, // nuova bozza
    };
    setSubmitting(true);
    newslettersApi
      .create(payload)
      .then((res) => {
        const id = res.data?.id ?? res.data?.newsletter?.id;
        showPopup(true, 'Bozza duplicata creata');
        setOpenDuplicate(false);
        if (id) {
          navigate('/admin/newsletter/newsletter-details', { state: { newsID: id } });
        } else {
          navigate('/admin/newsletter');
        }
      })
      .catch((err) => handleApiError(err))
      .finally(() => setSubmitting(false));
  };

  const onTestSend = () => {
    if (!testAddress) {
      showPopup('warning', 'Inserisci un indirizzo email per il test');
      return;
    }
    runAction(newslettersApi.testSend(newsID, testAddress), 'Email di test inviata', {
      close: () => setOpenTest(false),
    });
  };

  // Anteprima: carica l'HTML renderizzato dal BE (placeholder NON risolti).
  const openPreviewDialog = () => {
    setOpenPreview(true);
    setPreviewLoading(true);
    setPreviewHtml('');
    newslettersApi
      .preview(newsID)
      .then((res) => setPreviewHtml(res.data?.html ?? ''))
      .catch((err) => {
        setOpenPreview(false);
        handleApiError(err);
      })
      .finally(() => setPreviewLoading(false));
  };

  /* ------------------------------- Allegati ------------------------------- */
  const handleFilesPicked = ({ valid, rejected }) => {
    if (valid.length) setStagedFiles((prev) => [...prev, ...valid]);
    if (rejected.length) showPopup('warning', rejected.join(' · '));
  };
  const handleRemoveStaged = (index) => setStagedFiles((prev) => prev.filter((_, i) => i !== index));

  // Rimozione immediata di un allegato già caricato: rimuove SOLO da questa newsletter
  // (se ereditato dal template, il template non viene toccato — sono record separati).
  /**
   * Allega un file già in libreria: nessun caricamento, solo il collegamento,
   * poi si ricarica il dettaglio per mostrarlo fra gli allegati esistenti.
   */
  const handleAttachFromLibrary = (media) => {
    mediaApi
      .attachToNewsletter(newsID, media.id)
      .then((res) => {
        showPopup(true, res.status === 200 ? 'File già allegato' : 'Allegato aggiunto');
        refresh();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const handleRemoveExisting = (att) => {
    setRemovingAttId(att.id);
    newslettersApi
      .removeAttachment(newsID, att.id)
      .then(() => {
        setExistingAttachments((prev) => prev.filter((a) => a.id !== att.id));
        showPopup(true, 'Allegato rimosso');
      })
      .catch((err) => handleApiError(err))
      .finally(() => setRemovingAttId(null));
  };

  /* ------------------------------- Modifica inline ------------------------------- */
  const enterEdit = () => setIsEditing(true);

  const cancelEdit = () => {
    syncForm(newsletter); // discard modifiche form
    setStagedFiles([]); // discard allegati non caricati
    setIsEditing(false);
  };

  const onSave = async () => {
    if (!name.trim()) {
      showPopup('warning', 'Inserisci il nome della newsletter');
      return;
    }
    const payload = {
      name: name.trim(),
      sender_display_name: senderDisplayName?.trim() ? senderDisplayName.trim() : null,
      subject: subject.trim(),
      // Con un template che avvolge il contenuto la disiscrizione è nel template: non si duplica.
      body_html: inlineEmailHtml(templateWraps ? bodyHtml : ensureUnsubscribePlaceholder(bodyHtml)),
      reply_to_email_id: replyToEmailId,
      filter_id: recipientMode === 'filter' ? filterId : null,
      recipient_category: recipientMode === 'category' ? recipientCategory : null,
    };
    setSubmitting(true);
    try {
      await newslettersApi.update(newsID, payload);
      if (stagedFiles.length > 0) {
        await Promise.all(stagedFiles.map((file) => newslettersApi.addAttachment(newsID, buildAttachmentFormData(file))));
        setStagedFiles([]);
      }
      showPopup(true, 'Modifiche salvate');
      setIsEditing(false);
      refresh();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const status = getStatus(newsletter);
  const isEditable = EDITABLE.includes(status);
  const isScheduled = status === 'SCHEDULED';
  const isSending = status === 'SENDING';
  // Newsletter avviate all'invio. FAILED incluso: è il caso in cui serve di più arrivare
  // al dettaglio per destinatario, dove la colonna "Note" riporta il motivo dell'errore.
  const hasTracking = ['SENT', 'SENDING', 'STOPPED', 'FAILED'].includes(status);
  const entityId = getEntityId(newsletter);
  const readOnly = !isEditing;

  // Polling avanzamento invio: attivo solo in SENDING; a fine invio ricarica il dettaglio.
  const progress = useSendProgress(newsID, isSending, {
    onFinish: (data) => {
      if (data.status === 'SENT') showPopup(true, 'Invio completato');
      refresh();
    },
  });

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;
  if (!newsID) return <Navigate to={{ pathname: '/404' }} />;

  return (
    <>
      <Helmet>
        <title>Dettaglio Newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        {isApiCalled ? (
          errorMessage.length === 0 ? (
            <Stack direction="column" gap="24px">
              {/* Header + azioni */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap="12px"
              >
                <Stack direction="row" alignItems="center" gap="12px">
                  <DefaultOutlinedButton
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/admin/newsletter')}
                    disabled={submitting}
                  >
                    Indietro
                  </DefaultOutlinedButton>
                  <Typography variant="h5">{getName(newsletter) || 'Newsletter'}</Typography>
                  <StatusBadge status={status} size="medium" />
                </Stack>

                {isEditing ? (
                  <Stack direction="row" gap="12px">
                    <DefaultOutlinedButton onClick={cancelEdit} disabled={submitting}>
                      Annulla
                    </DefaultOutlinedButton>
                    <DefaultContainedButton onClick={onSave} disabled={submitting}>
                      Salva modifiche
                    </DefaultContainedButton>
                  </Stack>
                ) : (
                  <Stack direction="row" gap="12px" flexWrap="wrap" alignItems="center">
                    {/* Anteprima: pulsante per stati non editabili (per gli editabili è nel menu ⋮) */}
                    {!isEditable && (
                      <DefaultOutlinedButton
                        startIcon={<VisibilityOutlinedIcon />}
                        onClick={openPreviewDialog}
                        disabled={submitting}
                      >
                        Anteprima
                      </DefaultOutlinedButton>
                    )}

                    {/* Newsletter inviate/in invio/interrotte: accesso alle statistiche */}
                    {hasTracking && (
                      <DefaultOutlinedButton
                        startIcon={<InsightsIcon />}
                        onClick={() => navigate('/admin/newsletter/tracking', { state: { newsID } })}
                        disabled={submitting}
                      >
                        Statistiche
                      </DefaultOutlinedButton>
                    )}

                    {/* SENDING: unica azione */}
                    {isSending && (
                      <RedOutlinedButton onClick={onStop} disabled={submitting}>
                        Interrompi invio
                      </RedOutlinedButton>
                    )}

                    {/* CANCELLED: unica azione */}
                    {status === 'CANCELLED' && (
                      <DefaultContainedButton
                        startIcon={<ContentCopyIcon />}
                        onClick={() => setOpenDuplicate(true)}
                        disabled={submitting}
                      >
                        Duplica
                      </DefaultContainedButton>
                    )}

                    {/* DRAFT/SCHEDULED: azione primaria + secondaria + menu overflow */}
                    {isEditable && (
                      <>
                        <DefaultOutlinedButton onClick={enterEdit} disabled={submitting}>
                          Modifica
                        </DefaultOutlinedButton>
                        <DefaultContainedButton onClick={onSendNow} disabled={submitting}>
                          Invia ora
                        </DefaultContainedButton>
                        <Tooltip title="Altre azioni" arrow>
                          <span>
                            <IconButton
                              aria-label="Altre azioni"
                              disabled={submitting}
                              onClick={(e) => setActionsAnchor(e.currentTarget)}
                              sx={{ border: '1px solid #e0e4e7', borderRadius: '8px' }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Menu
                          anchorEl={actionsAnchor}
                          open={Boolean(actionsAnchor)}
                          onClose={closeActions}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          <MenuItem onClick={runFromMenu(openPreviewDialog)}>
                            <ListItemIcon>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Anteprima</ListItemText>
                          </MenuItem>
                          {status === 'DRAFT' && (
                            <MenuItem onClick={runFromMenu(() => setOpenSchedule(true))}>
                              <ListItemIcon>
                                <ScheduleIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Programma invio</ListItemText>
                            </MenuItem>
                          )}
                          {isScheduled && (
                            <MenuItem onClick={runFromMenu(onCancel)}>
                              <ListItemIcon>
                                <EventBusyIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Annulla programmazione</ListItemText>
                            </MenuItem>
                          )}
                          <MenuItem onClick={runFromMenu(() => setOpenTest(true))}>
                            <ListItemIcon>
                              <MailOutlineIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Invia email di test</ListItemText>
                          </MenuItem>
                          <Divider />
                          <MenuItem onClick={runFromMenu(() => setOpenDelete(true))} sx={{ color: '#B72136' }}>
                            <ListItemIcon>
                              <DeleteOutlineIcon fontSize="small" sx={{ color: '#B72136' }} />
                            </ListItemIcon>
                            <ListItemText>Elimina</ListItemText>
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </Stack>
                )}
              </Stack>

              {/* Avanzamento invio (solo SENDING) */}
              {isSending && (
                <SendProgressBar
                  percent={progress?.percent ?? newsletter?.metrics?.progress_percent}
                  sent={progress?.sent ?? newsletter?.metrics?.sent}
                  included={progress?.included ?? newsletter?.metrics?.included}
                />
              )}

              {/* Mittente */}
              <Card sx={{ padding: '24px' }}>
                <CardHeader sx={{ p: 0, mb: 2 }} title="Mittente" />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      label="Entità mittente"
                      value={newsletter.entity?.name ?? ''}
                      InputProps={{ readOnly: true }}
                      helperText={isEditing ? "L'entità mittente non è modificabile" : undefined}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      label="Nome mittente"
                      value={senderDisplayName}
                      InputProps={{ readOnly }}
                      onChange={(e) => setSenderDisplayName(e.currentTarget.value)}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Destinatari */}
              <RecipientSelector
                entityId={entityId}
                mode={recipientMode}
                onModeChange={setRecipientMode}
                filterId={filterId}
                onFilterChange={setFilterId}
                category={recipientCategory}
                onCategoryChange={setRecipientCategory}
                disabled={readOnly}
                allowPreview={isEditable}
              />

              {/* Contenuto */}
              <Card sx={{ padding: '24px' }}>
                <CardHeader sx={{ p: 0, mb: 2 }} title="Contenuto" />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      label="Nome interno"
                      value={name}
                      InputProps={{ readOnly }}
                      onChange={(e) => setName(e.currentTarget.value)}
                      helperText={isEditing ? 'Identifica la newsletter nella lista (non inviato)' : undefined}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextField
                      fullWidth
                      label="Oggetto"
                      value={subject}
                      InputProps={{ readOnly }}
                      onChange={(e) => setSubject(e.currentTarget.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {templateWraps ? 'Contenuto della newsletter' : 'Corpo della newsletter'}
                    </Typography>
                    <HtmlEditor
                      value={bodyHtml}
                      onChange={setBodyHtml}
                      disabled={readOnly}
                      entityId={entityId}
                      helperText={
                        templateWraps
                          ? 'Solo il contenuto: intestazione, chiusura e disiscrizione sono nel template e vengono aggiunte all\'invio. Usa "Anteprima" per vedere l\'email completa.'
                          : isEditing
                          ? 'Il link di disiscrizione viene aggiunto automaticamente.'
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Reply-to */}
              <Card sx={{ padding: '24px' }}>
                <CardHeader sx={{ p: 0, mb: 2 }} title="Indirizzo di risposta (Reply-To)" />
                <ReplyToSelector
                  entityId={entityId}
                  value={replyToEmailId}
                  onChange={setReplyToEmailId}
                  canWrite={isEditing}
                  disabled={readOnly}
                />
              </Card>

              {/* Allegati */}
              <AttachmentsCard
                existing={existingAttachments}
                staged={stagedFiles}
                onFilesPicked={handleFilesPicked}
                onRemoveStaged={handleRemoveStaged}
                onRemoveExisting={handleRemoveExisting}
                removingAttId={removingAttId}
                onPickFromLibrary={isEditing ? () => setMediaOpen(true) : undefined}
                disabled={readOnly}
              />

              {/* Qui la newsletter esiste già: il file di libreria viene collegato subito. */}
              <MediaLibraryDialog
                open={mediaOpen}
                mode="all"
                entityId={entityId}
                onClose={() => setMediaOpen(false)}
                onSelectMedia={handleAttachFromLibrary}
              />
            </Stack>
          ) : (
            ErrorUI(errorMessage)
          )
        ) : (
          LoadingUI()
        )}
      </Container>

      {/* Dialog: elimina */}
      <Dialog fullWidth open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>Eliminazione newsletter</DialogTitle>
        <DialogContent>
          <Typography>Sei sicuro di voler eliminare questa newsletter?</Typography>
        </DialogContent>
        <DialogActions>
          <DefaultOutlinedButton onClick={() => setOpenDelete(false)} disabled={submitting}>
            Annulla
          </DefaultOutlinedButton>
          <RedOutlinedButton onClick={onDelete} disabled={submitting}>
            Elimina
          </RedOutlinedButton>
        </DialogActions>
      </Dialog>

      {/* Dialog: programma */}
      <Dialog fullWidth open={openSchedule} onClose={() => setOpenSchedule(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>Programma invio</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                {/* minDate su OGGI: la giornata in corso è programmabile. */}
                <CustomDatePicker
                  minDate={minScheduleDate()}
                  label="Data invio *"
                  value={scheduleDate}
                  onChange={setScheduleDate}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                <CustomTimeField
                  label="Orario invio *"
                  minTime={isToday(scheduleDate) ? minScheduleDateTime() : undefined}
                  value={scheduleTime}
                  onChange={setScheduleTime}
                  format="HH:mm"
                  helperText={
                    isToday(scheduleDate) ? `Non prima delle ${minScheduleDateTime().format('HH:mm')}` : undefined
                  }
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <DefaultOutlinedButton onClick={() => setOpenSchedule(false)} disabled={submitting}>
            Annulla
          </DefaultOutlinedButton>
          <DefaultContainedButton onClick={onSchedule} disabled={submitting}>
            Programma
          </DefaultContainedButton>
        </DialogActions>
      </Dialog>

      {/* Dialog: invia test */}
      <Dialog fullWidth open={openTest} onClose={() => setOpenTest(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>Invia email di test</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <CustomTextField
              type="email"
              label="Indirizzo email *"
              value={testAddress}
              onChange={(e) => setTestAddress(e.currentTarget.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <DefaultOutlinedButton onClick={() => setOpenTest(false)} disabled={submitting}>
            Annulla
          </DefaultOutlinedButton>
          <DefaultContainedButton onClick={onTestSend} disabled={submitting}>
            Invia test
          </DefaultContainedButton>
        </DialogActions>
      </Dialog>

      {/* Dialog: duplica */}
      <Dialog fullWidth open={openDuplicate} onClose={() => setOpenDuplicate(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>Duplica newsletter</DialogTitle>
        <DialogContent>
          <Typography>
            Verrà creata una nuova <strong>bozza</strong> con gli stessi contenuti e destinatari, che potrai poi
            modificare e programmare o inviare. Vuoi procedere?
          </Typography>
        </DialogContent>
        <DialogActions>
          <DefaultOutlinedButton onClick={() => setOpenDuplicate(false)} disabled={submitting}>
            Annulla
          </DefaultOutlinedButton>
          <DefaultContainedButton onClick={onDuplicate} disabled={submitting}>
            Duplica
          </DefaultContainedButton>
        </DialogActions>
      </Dialog>

      {/* Dialog: anteprima */}
      <Dialog fullWidth maxWidth="md" open={openPreview} onClose={() => setOpenPreview(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>Anteprima</DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="iframe"
              title="Anteprima newsletter"
              sandbox=""
              srcDoc={previewHtml || '<p style="font-family:sans-serif;color:#888">Nessun contenuto</p>'}
              sx={{ width: '100%', height: '60vh', border: 'none' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <DefaultOutlinedButton onClick={() => setOpenPreview(false)}>Chiudi</DefaultOutlinedButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
